import { ReactNode } from 'react'
import { Anchor, Badge, Box, Center, Group, Image, Stack, Table, Text } from '@mantine/core'
import { IconPhoto } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { useAppTranslation } from 'hooks/useTranslation'
import { DeskQueueRow } from 'supabase/desk'
import { DESK_CHECK_NAMES, withRecheckChecks, withRestoreChecks } from './checkFieldMap'
import { CompareField, dateLocaleOf, DeskDateLocale, fromApplication, fromQueueTarget, proposalChanges } from './compareRows'
import { DeskTab, discardWhy } from './deskTabs'
import { publicImageUrl, shownImagePath } from './deskImages'

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * The desk's table, one column set per tab (UI-SPEC §B). Every cell renders
 * untrusted text (titles, post excerpts, editor reasons) as plain `Text`
 * children, never as HTML (T-33-53).
 *
 * Row actions (the Reject menu, Restore) come in through `renderActions`, so
 * the decision wiring stays on the page; an action must stop the click from
 * reaching the row.
 */
export type DeskTableProps = {
  tab: DeskTab
  rows: DeskQueueRow[]
  onOpenCandidate: (row: DeskQueueRow) => void
  onOpenRawItem: (row: DeskQueueRow) => void
  renderActions?: (row: DeskQueueRow) => ReactNode
}

/** One key per row: a candidate by its id, a discarded raw item by its raw item id. */
export const deskRowKey = (row: Pick<DeskQueueRow, 'application_id' | 'raw_item_id'>): string =>
  row.application_id !== null ? `a${row.application_id}` : `r${row.raw_item_id}`

/** A discarded raw item with no candidate: it opens a modal, not /desk/:id. */
export const isRawItemRow = (row: Pick<DeskQueueRow, 'application_id'>): boolean =>
  row.application_id === null

// Counted in characters (code points), not UTF-16 units: a cut through a
// 4-byte letter would leave a lone surrogate, drawn as U+FFFD.
const RAW_EXCERPT_LENGTH = 80

const clean = (value: string | null | undefined): string | null => {
  const text = value?.trim()
  return text ? text : null
}

/**
 * 'ddd, DD/MM/YYYY HH:mm' from the stored Lisbon wall clock (no zone shift), or
 * null. The weekday is written in `locale` (the admin language, D-22; en by default).
 */
export const formatStarts = (date: string | null, time: string | null, locale: DeskDateLocale = 'en'): string | null => {
  if (!date) return null
  const clock = time ? /^(\d{2}):(\d{2})/.exec(time) : null
  return clock
    ? dayjs(`${date}T${clock[1]}:${clock[2]}`).locale(locale).format('ddd, DD/MM/YYYY HH:mm')
    : dayjs(date).locale(locale).format('ddd, DD/MM/YYYY')
}

/** 'venue · city'; a missing part is left out; null when both are missing. */
export const formatPlace = (venue: string | null, city: string | null): string | null => {
  const parts = [clean(venue), clean(city)].filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** route_checks in the editor's display order; names the map does not know go last, as is. */
export const orderedChecks = (checks: readonly string[] | null): string[] => {
  const list = checks ?? []
  const known = DESK_CHECK_NAMES.filter((name) => list.includes(name))
  const unknown = list.filter((name) => !(DESK_CHECK_NAMES as readonly string[]).includes(name))
  return [...known, ...unknown]
}

const COMPARE_FIELD_LABEL_KEYS: Record<CompareField, string> = {
  title: 'events.event_title',
  start: 'desk.fields.start',
  end: 'desk.fields.end',
  place: 'desk.fields.place',
  price: 'events.price',
  status: 'desk.fields.status'
}

const checkLabel = (t: Translate, name: string): string =>
  (DESK_CHECK_NAMES as readonly string[]).includes(name) ? t(`desk.checks.${name}`) : name

const REJECT_REASONS = ['not_an_event', 'wrong_area', 'duplicate', 'past', 'other']

/** The Why text of a Discard pile row (desk_queue.discard_kind / discard_reason). */
export const whyText = (t: Translate, row: Pick<DeskQueueRow, 'discard_kind' | 'discard_reason'>): string | null => {
  const why = discardWhy(row.discard_kind, row.discard_reason)
  if (!why) return null
  if (row.discard_kind === 'rejected') {
    const reason = why.reason && REJECT_REASONS.includes(why.reason)
      ? t(`desk.reject.reasons.${why.reason}`)
      : why.reason ?? t('desk.empty_value')
    return t(why.key, { reason })
  }
  return why.reason ? t(why.key, { reason: why.reason }) : t(why.key)
}

const Dimmed = ({ children }: { children: ReactNode }) => (
  <Text size="sm" c="dimmed">
    {children}
  </Text>
)

const Thumb = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  const path = shownImagePath(row)
  if (!path) {
    return (
      <Center
        data-testid="desk-thumb-empty"
        w={48}
        h={48}
        bg="gray.0"
        style={{ borderRadius: 4 }}
      >
        <IconPhoto size={16} color="var(--mantine-color-gray-5)" />
      </Center>
    )
  }
  return <Image src={publicImageUrl(path)} alt={t('desk.images.shown')} w={48} h={48} fit="cover" radius="sm" />
}

const TitleCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  if (isRawItemRow(row)) {
    const excerpt = clean(row.raw_excerpt)
    if (!excerpt) return <Dimmed>{t('desk.candidate.no_text')}</Dimmed>
    return (
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {Array.from(excerpt).slice(0, RAW_EXCERPT_LENGTH).join('')}
      </Text>
    )
  }
  const title = clean(row.event_title)
  if (!title) {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        {t('desk.untitled')}
      </Text>
    )
  }
  return <Text size="sm">{title}</Text>
}

const StartsCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  const { language } = useAppTranslation()
  const starts = formatStarts(row.starting_date, row.starting_time, dateLocaleOf(language))
  return starts ? <Text size="sm">{starts}</Text> : <Dimmed>{t('desk.no_date')}</Dimmed>
}

const PlaceCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  const place = formatPlace(row.event_venue_location, row.venue_village)
  return place ? <Text size="sm">{place}</Text> : <Dimmed>{t('desk.no_place')}</Dimmed>
}

export const SourceBadge = ({ source, beat }: { source: string | null; beat: string | null }) => {
  const { t } = useAppTranslation()
  if (source === 'paste') {
    return (
      <Badge size="sm" fz={12} variant="light" color="violet">
        {t('desk.source.pasted')}
      </Badge>
    )
  }
  return (
    <Stack gap={4} align="flex-start">
      <Badge size="sm" fz={12} variant="outline" color="gray">
        {t('desk.source.facebook')}
      </Badge>
      {clean(beat) && (
        <Text size="xs" lh={1.4} c="dimmed">
          {beat}
        </Text>
      )}
    </Stack>
  )
}

const ChecksCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  const checks = orderedChecks(withRecheckChecks(withRestoreChecks(row)).route_checks)
  const shown = checks.slice(0, 2)
  const more = checks.length - shown.length
  return (
    <Group gap={8}>
      {shown.map((name) => (
        <Badge key={name} size="sm" fz={12} variant="light" color="orange">
          {checkLabel(t, name)}
        </Badge>
      ))}
      {more > 0 && (
        <Badge size="sm" fz={12} variant="light" color="gray">
          {t('desk.more_checks', { count: more })}
        </Badge>
      )}
    </Group>
  )
}

const UpdatesCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  const title = clean(row.target_event_title) ?? clean(row.target_event_title_pt)
  if (!title) return <Dimmed>{t('desk.untitled')}</Dimmed>
  if (!row.target_event_uid) return <Text size="sm">{title}</Text>
  return (
    <Anchor
      component={Link}
      size="sm"
      to={`/Events/${row.target_event_uid}`}
      onClick={(event) => event.stopPropagation()}
    >
      {title}
    </Anchor>
  )
}

const ChangesCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  const changes = proposalChanges(fromQueueTarget(row), fromApplication(row))
  return (
    <Group gap={8}>
      {changes.map((field) => (
        <Badge key={field} size="sm" fz={12} variant="light" color="yellow">
          {t(COMPARE_FIELD_LABEL_KEYS[field])}
        </Badge>
      ))}
    </Group>
  )
}

const WhyCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) => {
  const why = whyText(t, row)
  return why ? (
    <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
      {why}
    </Text>
  ) : (
    <Dimmed>{t('desk.empty_value')}</Dimmed>
  )
}

const DiscardedCell = ({ row, t }: { row: DeskQueueRow; t: Translate }) =>
  row.discarded_at ? (
    <Text size="xs" lh={1.4}>
      {dayjs(row.discarded_at).format('DD/MM/YYYY HH:mm')}
    </Text>
  ) : (
    <Dimmed>{t('desk.empty_value')}</Dimmed>
  )

type Column = {
  key: string
  headerKey: string | null
  width?: number
  render: (row: DeskQueueRow, t: Translate) => ReactNode
}

const thumb: Column = { key: 'thumb', headerKey: null, width: 64, render: (row, t) => <Thumb row={row} t={t} /> }
const title: Column = { key: 'title', headerKey: 'desk.columns.title', render: (row, t) => <TitleCell row={row} t={t} /> }
const starts: Column = { key: 'starts', headerKey: 'desk.columns.starts', render: (row, t) => <StartsCell row={row} t={t} /> }
const place: Column = { key: 'place', headerKey: 'desk.columns.place', render: (row, t) => <PlaceCell row={row} t={t} /> }
const source: Column = {
  key: 'source',
  headerKey: 'desk.columns.source',
  render: (row) => <SourceBadge source={row.source} beat={row.beat} />
}
const checks: Column = { key: 'checks', headerKey: 'desk.columns.checks', render: (row, t) => <ChecksCell row={row} t={t} /> }
const updates: Column = { key: 'updates', headerKey: 'desk.columns.updates', render: (row, t) => <UpdatesCell row={row} t={t} /> }
const changes: Column = { key: 'changes', headerKey: 'desk.columns.changes', render: (row, t) => <ChangesCell row={row} t={t} /> }
const why: Column = { key: 'why', headerKey: 'desk.columns.why', render: (row, t) => <WhyCell row={row} t={t} /> }
const discarded: Column = {
  key: 'discarded',
  headerKey: 'desk.columns.discarded',
  render: (row, t) => <DiscardedCell row={row} t={t} />
}

/** UI-SPEC §B column sets. `actions` is appended where the tab has row actions. */
const TAB_COLUMNS: Record<DeskTab, { columns: Column[]; actions: boolean }> = {
  ready: { columns: [thumb, title, starts, place, source], actions: true },
  needs_a_look: { columns: [thumb, title, starts, place, checks, source], actions: true },
  updates: { columns: [thumb, title, updates, changes, starts, source], actions: false },
  discard: { columns: [thumb, title, why, discarded, source], actions: true }
}

export const DeskTable = ({ tab, rows, onOpenCandidate, onOpenRawItem, renderActions }: DeskTableProps) => {
  const { t } = useAppTranslation()
  const { columns, actions } = TAB_COLUMNS[tab]
  const withActions = actions && renderActions !== undefined

  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table highlightOnHover verticalSpacing={8} horizontalSpacing={16} miw={800}>
        <Table.Thead>
          <Table.Tr>
            {columns.map((column) => (
              <Table.Th key={column.key} style={column.width ? { width: column.width } : undefined}>
                {column.headerKey ? t(column.headerKey) : null}
              </Table.Th>
            ))}
            {withActions && <Table.Th style={{ width: 120 }}>{t('common.actions')}</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr
              key={deskRowKey(row)}
              style={{ cursor: 'pointer' }}
              onClick={() => (isRawItemRow(row) ? onOpenRawItem(row) : onOpenCandidate(row))}
            >
              {columns.map((column) => (
                <Table.Td key={column.key}>{column.render(row, t)}</Table.Td>
              ))}
              {withActions && <Table.Td>{renderActions(row)}</Table.Td>}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  )
}
